import type { Vector3 } from 'three';

/**
 * Where the camera orbits around.
 *
 * OrbitControls uses one vector, `target`, as both the pivot and the look-at
 * point, so moving the pivot off the view axis would swing the camera round to
 * face it. These two helpers are the two honest ways to move a pivot without
 * that happening: keep it on the axis, or pan the camera with it.
 */

// Far enough in front of the camera that the orbit cannot invert.
const MIN_DEPTH = 0.01;

/**
 * The point on the camera's view axis level with `point`.
 *
 * Orbiting then swings around the depth of whatever was picked while the view
 * stays perfectly still, because the pivot never leaves the centre of the
 * screen.
 */
export function pivotAtDepth(cameraPos: Vector3, viewDir: Vector3, point: Vector3): Vector3 {
  const forward = viewDir.clone().normalize();
  const depth = Math.max(point.clone().sub(cameraPos).dot(forward), MIN_DEPTH);
  return forward.multiplyScalar(depth).add(cameraPos);
}

/**
 * Pivot on `point` by panning: the camera moves by the same delta as the
 * target, so the view direction and distance are untouched and the point ends
 * up in the middle of the screen.
 */
export function recenterOn(
  cameraPos: Vector3,
  target: Vector3,
  point: Vector3,
): { position: Vector3; target: Vector3 } {
  const delta = point.clone().sub(target);
  return { position: cameraPos.clone().add(delta), target: point.clone() };
}
